import { IVFSResponse, Tag } from '../types';
import BaseAPI from '../BaseAPI';
import {
  SignUpResponseData,
  SignInResponseData,
  UpdateProfileResponseData,
  InitialDataResponse,
} from './types';
import { InitialRequestOptions } from '../../../../features/app/appSlice';

/**
 * Auth API
 * Handles authentication and user-related operations
 */
class AuthAPI extends BaseAPI {
  /**
   * Sign up a new user
   * @param data - User signup data
   */
  async signUp(data: {
    name: string;
    email: string;
    password: string;
    redirectUrl?: string;
  }): Promise<IVFSResponse<SignUpResponseData>> {
    return this.wrapResponse<SignUpResponseData>({
      promise: this.apiInstance.post<IVFSResponse<SignUpResponseData>>(
        '/v2/account/register',
        data
      ),
      errorMessage: 'Failed to sign up',
    });
  }

  /**
   * Sign in an existing user
   * @param options - User credentials
   */
  async signIn(options: {
    email: string;
    password: string;
  }): Promise<IVFSResponse<SignInResponseData>> {
    return this.wrapResponse<SignInResponseData>({
      promise: this.apiInstance.post<IVFSResponse<SignInResponseData>>(
        '/v2/account/login',
        options
      ),
      errorMessage: 'Failed to sign in',
    });
  }

  /**
   * Request a password reset
   * @param email - User email
   */
  async forgotPassword({
    email,
  }: {
    email: string;
  }): Promise<IVFSResponse<undefined>> {
    return this.wrapResponse({
      promise: this.apiInstance.post('/v2/account/password/reset/request', {
        email,
      }),
      errorMessage: 'Failed to request password reset',
    });
  }

  /**
   * Reset a user's password
   * @param data - Password reset data
   */
  async resetPassword(data: {
    password: string;
    confirm: string;
    token: string;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post('/v2/account/password/reset', data),
      errorMessage: 'Failed to reset password',
    });
  }

  /**
   * Get initial user data
   * @param options - Optional parameters
   */
  async getInitialData(
    options: InitialRequestOptions = {}
  ): Promise<IVFSResponse<InitialDataResponse>> {
    const queryParams = new URLSearchParams();
    if (options.joinCode) queryParams.append('joinCode', options.joinCode);
    if (options.zoomCode) queryParams.append('zoomCode', options.zoomCode);
    if (options.state) queryParams.append('state', options.state);

    return this.wrapResponse({
      promise: this.apiInstance.get(
        `/v2/init${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      ),
      errorMessage: 'Failed to get initial data',
    });
  }

  /**
   * Schedule account deletion
   */
  async scheduleAccountDeletion(): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.put('/v2/account/deletion-schedule'),
      errorMessage: 'Failed to schedule account deletion',
    });
  }

  /**
   * Cancel scheduled account deletion
   */
  async cancelAccountDeletion(): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.delete('/v2/account/deletion-schedule'),
      errorMessage: 'Failed to cancel account deletion',
    });
  }

  /**
   * Add a tag to the user
   * @param tag - Tag data
   */
  async addUserTag(tag: Tag): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post('/v2/account/tag', tag),
      errorMessage: 'Failed to add user tag',
    });
  }

  /**
   * Update user profile
   * @param data - Profile data
   */
  async updateProfile(data: {
    name: string;
    pictureMime?: string;
    picture?: string;
  }): Promise<IVFSResponse<UpdateProfileResponseData>> {
    return this.wrapResponse<UpdateProfileResponseData>({
      promise: this.apiInstance.put('/v2/account/profile', data),
      errorMessage: 'Failed to update profile',
    });
  }

  /**
   * Get user settings
   * @param key - Settings key
   */
  async getSettings(
    key: 'recorder' | 'plugin' | 'user' | 'ui'
  ): Promise<IVFSResponse<any>> {
    return this.wrapResponse<any>({
      promise: this.apiInstance.get(`/v2/settings/${key}`),
      errorMessage: `Failed to get ${key} settings`,
    });
  }

  /**
   * Update user settings
   * @param key - Settings key
   * @param newSettings - New settings data
   */
  async updateSettings(
    key: string,
    newSettings: any
  ): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post(`/v2/settings/${key}`, newSettings),
      errorMessage: `Failed to update ${key} settings`,
    });
  }

  /**
   * Get output language setting
   */
  async getOutputLanguage(): Promise<IVFSResponse<{ outputLanguage: string }>> {
    return this.wrapResponse<{ outputLanguage: string }>({
      promise: this.apiInstance.get('/v2/settings/output-language'),
      errorMessage: 'Failed to get output language setting',
    });
  }

  /**
   * Set output language setting
   * @param outputLanguage - The language code
   */
  async setOutputLanguage(outputLanguage: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post('/v2/settings/output-language', {
        outputLanguage,
      }),
      errorMessage: 'Failed to set output language',
    });
  }
}

export default AuthAPI;
